<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { Loader2, ShieldCheck, AlertCircle, CheckCircle2 } from 'lucide-vue-next'

const status = ref<'loading' | 'success' | 'error'>('loading')
const message = ref('Procesando autenticación…')

const title = computed(() => {
  if (status.value === 'success') return 'Autenticación completada'
  if (status.value === 'error') return 'Error de autenticación'
  return 'Autenticando'
})

onMounted(async () => {
  const query = new URLSearchParams(window.location.search)
  const code = query.get('code')
  const provider = query.get('state') || query.get('provider')
  const error = query.get('error')
  const redirectUri = `${window.location.origin}/oauth/callback`

  if (error) {
    status.value = 'error'
    message.value = `OAuth rechazado: ${error}`
    window.opener?.postMessage({ type: 'oauth-error', message: error }, window.location.origin)
    window.close()
    return
  }

  if (!code || !provider) {
    status.value = 'error'
    message.value = 'No se recibió código o proveedor OAuth'
    window.opener?.postMessage({ type: 'oauth-error', message: message.value }, window.location.origin)
    window.close()
    return
  }

  try {
    const response = await fetch(
      `/api/v1/auth/oauth/callback?provider=${encodeURIComponent(provider)}&code=${encodeURIComponent(code)}&redirect_uri=${encodeURIComponent(redirectUri)}`,
      { method: 'POST' }
    )
    const data = await response.json()

    if (!response.ok) {
      throw new Error(data.detail || 'No se pudo completar el acceso con OAuth')
    }

    status.value = 'success'
    message.value = 'Acceso completado. Puedes volver a la aplicación.'
    window.opener?.postMessage({ type: 'oauth-success', ...data }, window.location.origin)
    window.close()
  } catch (error: any) {
    status.value = 'error'
    message.value = error.message || 'No se pudo completar el acceso con OAuth'
    window.opener?.postMessage({ type: 'oauth-error', message: message.value }, window.location.origin)
    window.close()
  }
})
</script>

<template>
  <main class="min-h-screen grid place-items-center bg-background p-4 sm:p-6">
    <div class="flex flex-col items-center text-center space-y-6 max-w-sm sm:max-w-md">
      <div class="relative">
        <div
          v-if="status === 'loading'"
          class="grid h-16 w-16 place-items-center rounded-full bg-primary/10"
        >
          <Loader2 class="size-8 animate-spin text-primary" />
        </div>
        <div
          v-else-if="status === 'success'"
          class="grid h-16 w-16 place-items-center rounded-full bg-primary/10"
        >
          <CheckCircle2 class="size-8 text-primary" />
        </div>
        <div
          v-else
          class="grid h-16 w-16 place-items-center rounded-full bg-destructive/10"
        >
          <AlertCircle class="size-8 text-destructive" />
        </div>
      </div>

      <div class="space-y-1.5">
        <div class="flex items-center justify-center gap-2">
          <ShieldCheck class="size-4 text-muted-foreground" />
          <h1 class="text-xl font-semibold tracking-tight">{{ title }}</h1>
        </div>
        <p class="text-sm text-muted-foreground">{{ message }}</p>
      </div>

      <p v-if="status === 'loading'" class="text-xs text-muted-foreground">
        No cierres esta ventana. Serás redirigido automáticamente.
      </p>
    </div>
  </main>
</template>